/**
 * Mode Toggle Field Component
 *
 * A field that supports toggling between:
 * - Text mode: Manual text entry with expression/variable interpolation support
 * - Data mode: Dropdown populated dynamically from the connected service
 *
 * The toggle appears on hover/focus only for a clean UI.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { HelpCircle, RefreshCw, Loader2, Type, Database, Check, ChevronsUpDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { InferredPropertySchema } from '@/types/inferred-schema';
import type { ModeToggleConfig } from '@/shared/types/node-properties';
import { DroppableInput } from '../../node-editor/DroppableInput';
import { nodeOptionsApi } from '@/lib/api';
import { cn } from '@/lib/utils';

interface ModeToggleFieldProps {
  property: InferredPropertySchema & { modeToggle?: ModeToggleConfig };
  value: string;
  onChange: (value: string) => void;
  onModeChange?: (fieldKey: string, mode: 'text' | 'data') => void;
  error?: string;
  nodeType?: string;
  credentialId?: string;
  allParameters?: Record<string, any>;
}

interface OptionItem {
  value: string;
  label: string;
  description?: string;
}

// Cache for loaded options to avoid unnecessary API calls
const optionsCache = new Map<string, { options: OptionItem[]; expiry: number }>();

export function ModeToggleField({
  property,
  value,
  onChange,
  onModeChange,
  error,
  nodeType,
  credentialId,
  allParameters,
}: ModeToggleFieldProps) {
  const modeToggle = property.modeToggle;
  const defaultMode = modeToggle?.defaultMode || 'text';

  // Get saved mode from parameters, or use default
  const savedMode = allParameters?.[`${property.key}_mode`] as 'text' | 'data' | undefined;
  const initialMode = savedMode || defaultMode;

  const [mode, setMode] = useState<'text' | 'data'>(initialMode);
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [autoLoadComplete, setAutoLoadComplete] = useState(false); // True when we've loaded initial pages
  const containerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false); // Prevent duplicate fetches
  const pagesLoadedRef = useRef(0); // Track pages loaded in ref to avoid stale closures
  const prevCredentialIdRef = useRef<string | undefined>(credentialId); // Track credential changes

  // Number of pages to auto-load initially (5 pages = 250 items for 50/page APIs)
  const AUTO_LOAD_PAGES = 5;

  // Get dependent field values
  const getDependentValues = useCallback(() => {
    if (!modeToggle?.loadOptions.dependsOn || !allParameters) {
      return undefined;
    }
    const values: Record<string, any> = {};
    for (const key of modeToggle.loadOptions.dependsOn) {
      if (allParameters[key] !== undefined) {
        values[key] = allParameters[key];
      }
    }
    return Object.keys(values).length > 0 ? values : undefined;
  }, [modeToggle?.loadOptions.dependsOn, allParameters]);

  // Resolve the effective node type: use loadOptions.nodeType override if set,
  // otherwise fall back to the current node's type. This allows trigger nodes
  // (e.g., asana-trigger) to reuse the main node's options provider (asana).
  const effectiveNodeType = modeToggle?.loadOptions.nodeType || nodeType;

  // Generate cache key for options
  const getCacheKey = useCallback(() => {
    const dependentValues = getDependentValues();
    return JSON.stringify({
      nodeType: effectiveNodeType,
      method: modeToggle?.loadOptions.method,
      credentialId,
      dependentValues,
    });
  }, [effectiveNodeType, modeToggle?.loadOptions.method, credentialId, getDependentValues]);

  // Load more options (for pagination)
  // When isManualLoad is true, resets page count for another batch (user clicked "Load more")
  const loadMoreOptions = useCallback(async (token: string, isManualLoad = false) => {
    const requiresCredential = modeToggle?.loadOptions.requiresCredential !== false;
    if (!modeToggle || !effectiveNodeType || (requiresCredential && !credentialId)) {
      return;
    }

    // Guard against duplicate calls
    if (loadingMoreRef.current) {
      return;
    }

    loadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const response = await nodeOptionsApi.loadOptions({
        nodeType: effectiveNodeType,
        method: modeToggle.loadOptions.method,
        credentialId: credentialId || '00000000-0000-0000-0000-000000000000', // Pass empty GUID when no credential needed
        dependentValues: getDependentValues(),
        continuationToken: token,
      });

      const data = response.data;
      if (data.success) {
        // Append new options to existing ones
        setOptions(prev => [...prev, ...(data.options || [])]);

        // Update page count using ref to avoid stale closure issues
        pagesLoadedRef.current += 1;
        setPagesLoaded(pagesLoadedRef.current);

        // Update pagination state
        setHasMore(data.hasMore || false);
        setContinuationToken(data.continuationToken || null);

        // Check if we should mark auto-load as complete
        const shouldStopAutoLoad = isManualLoad || pagesLoadedRef.current >= AUTO_LOAD_PAGES || !data.hasMore;

        if (shouldStopAutoLoad) {
          // We've either loaded all pages, hit our limit, or this was a manual load
          setAutoLoadComplete(true);

          // Sort options unless preserveOrder is set (e.g., for most recent chats)
          const shouldPreserveOrder = modeToggle.loadOptions.preserveOrder === true;
          setOptions(prev => {
            const finalOptions = shouldPreserveOrder
              ? prev
              : [...prev].sort((a, b) =>
                  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
                );

            // Only cache if we've loaded everything (no more pages)
            if (!data.hasMore) {
              const cacheKey = getCacheKey();
              const cacheTtl = modeToggle.loadOptions.cacheTtl ?? 60000;
              const shouldCache = modeToggle.loadOptions.cacheResults !== false;
              if (shouldCache) {
                optionsCache.set(cacheKey, {
                  options: finalOptions,
                  expiry: Date.now() + cacheTtl,
                });
              }
            }

            return finalOptions;
          });
        }
        // Note: If more pages should be auto-loaded, the useEffect will trigger the next call
      }
    } catch (err: any) {
      console.error('Failed to load more options:', err);
      // Don't clear existing options on pagination error
      setAutoLoadComplete(true); // Stop trying on error
    } finally {
      setIsLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [modeToggle, effectiveNodeType, credentialId, getDependentValues, getCacheKey]);

  // Handle manual "Load more" click - load another batch of pages
  const handleLoadMore = useCallback(() => {
    if (continuationToken && !isLoadingMore && !loadingMoreRef.current) {
      // Reset auto-load state to allow another batch
      setAutoLoadComplete(false);
      pagesLoadedRef.current = 0;
      setPagesLoaded(0);
      loadMoreOptions(continuationToken, true);
    }
  }, [continuationToken, isLoadingMore, loadMoreOptions]);

  // Load options from backend
  const loadOptions = useCallback(async (forceRefresh = false) => {
    if (!modeToggle || !effectiveNodeType) {
      return;
    }

    const requiresCredential = modeToggle.loadOptions.requiresCredential !== false;
    if (requiresCredential && !credentialId) {
      setLoadError('Please select a credential first');
      setOptions([]);
      return;
    }

    const cacheKey = getCacheKey();
    const cacheTtl = modeToggle.loadOptions.cacheTtl ?? 60000;
    const shouldCache = modeToggle.loadOptions.cacheResults !== false;

    // Check cache first (unless force refresh)
    if (!forceRefresh && shouldCache) {
      const cached = optionsCache.get(cacheKey);
      if (cached && cached.expiry > Date.now()) {
        setOptions(cached.options);
        setHasMore(false);
        setContinuationToken(null);
        setAutoLoadComplete(true);
        setLoadError(null);
        return;
      }
    }

    // Check if dependent fields are filled
    const dependentValues = getDependentValues();
    if (modeToggle.loadOptions.dependsOn && modeToggle.loadOptions.dependsOn.length > 0) {
      const missingDeps = modeToggle.loadOptions.dependsOn.filter(
        (key) => !dependentValues || !dependentValues[key]
      );
      if (missingDeps.length > 0) {
        setOptions([]);
        setLoadError(`Please select ${missingDeps.join(', ')} first`);
        return;
      }
    }

    setIsLoading(true);
    setLoadError(null);
    setHasMore(false);
    setContinuationToken(null);
    setPagesLoaded(1); // First page
    setAutoLoadComplete(false);
    loadingMoreRef.current = false;

    try {
      const response = await nodeOptionsApi.loadOptions({
        nodeType: effectiveNodeType,
        method: modeToggle.loadOptions.method,
        credentialId: credentialId || '00000000-0000-0000-0000-000000000000', // Pass empty GUID when no credential needed
        dependentValues,
      });

      const data = response.data;
      if (data.success) {
        setOptions(data.options || []);
        setLoadError(null);

        // Handle pagination - if more results available, start loading them in background
        if (data.hasMore && data.continuationToken) {
          setHasMore(true);
          setContinuationToken(data.continuationToken);
        } else {
          // No pagination needed - sort (unless preserveOrder) and cache immediately
          const shouldPreserveOrder = modeToggle.loadOptions.preserveOrder === true;
          const finalOptions = shouldPreserveOrder
            ? (data.options || [])
            : [...(data.options || [])].sort((a, b) =>
                a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
              );
          setOptions(finalOptions);

          // Cache the results
          if (shouldCache) {
            optionsCache.set(cacheKey, {
              options: finalOptions,
              expiry: Date.now() + cacheTtl,
            });
          }
        }
      } else {
        setOptions([]);
        setLoadError(data.error || 'Failed to load options');
      }
    } catch (err: any) {
      console.error('Failed to load options:', {
        error: err,
        nodeType: effectiveNodeType,
        method: modeToggle.loadOptions.method,
        status: err.response?.status,
        responseData: err.response?.data,
      });
      setOptions([]);
      setLoadError(err.response?.data?.error || err.message || 'Failed to load options');
    } finally {
      setIsLoading(false);
    }
  }, [modeToggle, effectiveNodeType, credentialId, getCacheKey, getDependentValues]);

  // Auto-load more when we have a continuation token (up to AUTO_LOAD_PAGES)
  useEffect(() => {
    if (hasMore && continuationToken && !isLoading && !loadingMoreRef.current && !autoLoadComplete && pagesLoaded < AUTO_LOAD_PAGES) {
      loadMoreOptions(continuationToken, false);
    }
  }, [hasMore, continuationToken, isLoading, loadMoreOptions, autoLoadComplete, pagesLoaded, AUTO_LOAD_PAGES]);

  // Reset error state when credential changes (not on initial mount)
  // This allows retry after transient errors when user switches credentials
  useEffect(() => {
    if (prevCredentialIdRef.current !== undefined && prevCredentialIdRef.current !== credentialId) {
      setLoadError(null);
      setOptions([]);
      setHasMore(false);
      setContinuationToken(null);
      setAutoLoadComplete(false);
      pagesLoadedRef.current = 0;
      setPagesLoaded(0);
    }
    prevCredentialIdRef.current = credentialId;
  }, [credentialId]);

  // Load options when in data mode (pre-load when node properties open)
  useEffect(() => {
    if (mode === 'data' && options.length === 0 && !isLoading && !loadError) {
      loadOptions();
    }
  }, [mode, options.length, isLoading, loadError, loadOptions]);

  // Reload options when dependencies change
  useEffect(() => {
    if (mode === 'data' && modeToggle?.loadOptions.dependsOn) {
      // Clear and reload options when dependencies change
      setOptions([]);
      setHasMore(false);
      setContinuationToken(null);
      setAutoLoadComplete(false);
      setLoadError(null); // Clear any previous error to allow retry
      pagesLoadedRef.current = 0;
      setPagesLoaded(0);
    }
  }, [allParameters, modeToggle?.loadOptions.dependsOn]);

  // Handle mode switch
  const handleModeSwitch = (newMode: 'text' | 'data') => {
    setMode(newMode);
    // Persist the mode setting
    if (onModeChange) {
      onModeChange(property.key, newMode);
    }
    // Don't clear the value when switching modes - preserve it
  };

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(query) ||
      (opt.description && opt.description.toLowerCase().includes(query))
    );
  }, [options, searchQuery]);

  // Get selected option label for display
  const selectedLabel = useMemo(() => {
    if (!value) return null;
    const selected = options.find((opt) => opt.value === value);
    return selected?.label || null;
  }, [value, options]);

  // Handle combobox selection
  const handleComboboxSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setComboboxOpen(false);
    setSearchQuery('');
  };

  // Show toggle controls only on hover/focus
  const showToggle = isHovered || isFocused;

  const textModeLabel = modeToggle?.textModeLabel || 'Text';
  const dataModeLabel = modeToggle?.dataModeLabel || 'Data';

  return (
    <div
      ref={containerRef}
      className="space-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        // Only unfocus if focus moves outside the container
        if (!containerRef.current?.contains(e.relatedTarget as Node)) {
          setIsFocused(false);
        }
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label htmlFor={property.key}>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.helpText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{property.helpText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Mode toggle - visible on hover/focus */}
        <div
          className={cn(
            'flex items-center gap-1 transition-opacity duration-200',
            showToggle ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={mode === 'text' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => handleModeSwitch('text')}
                >
                  <Type className="h-3 w-3 mr-1" />
                  {textModeLabel}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Enter text or expressions manually</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant={mode === 'data' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => handleModeSwitch('data')}
                >
                  <Database className="h-3 w-3 mr-1" />
                  {dataModeLabel}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Select from available options</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Refresh button for data mode */}
          {mode === 'data' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => loadOptions(true)}
                    disabled={isLoading}
                  >
                    <RefreshCw className={cn('h-3 w-3', isLoading && 'animate-spin')} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Refresh options</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {property.description && (
        <p className="text-sm text-muted-foreground">{property.description}</p>
      )}

      {/* Text mode - DroppableInput with expression support */}
      {mode === 'text' && (
        <DroppableInput
          id={property.key}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={property.placeholder}
          className={error ? 'border-destructive' : ''}
        />
      )}

      {/* Data mode - Searchable combobox dropdown */}
      {mode === 'data' && (
        <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={comboboxOpen}
              className={cn(
                "w-full justify-between font-normal",
                error || loadError ? 'border-destructive' : '',
                !value && "text-muted-foreground"
              )}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : loadError ? (
                <span className="truncate">{loadError}</span>
              ) : (
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="truncate">
                    {selectedLabel || modeToggle?.dataModePlaceholder || `Choose ${property.label.toLowerCase()}...`}
                  </span>
                  {isLoadingMore && (
                    <Loader2 className="h-3 w-3 animate-spin shrink-0 text-muted-foreground" />
                  )}
                </div>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] max-w-[min(var(--radix-popover-trigger-width),calc(100vw-2rem))] p-0 overflow-hidden"
            align="start"
            side="bottom"
            sideOffset={4}
            avoidCollisions={true}
            collisionPadding={16}
          >
            <Command shouldFilter={false} className="max-w-full">
              <CommandInput
                placeholder={`Search ${property.label?.toLowerCase() || 'options'}...`}
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>
                  {options.length === 0 ? 'No options available' : 'No matching options'}
                </CommandEmpty>
                <CommandGroup>
                  {searchQuery && filteredOptions.length !== options.length && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground border-b">
                      Showing {filteredOptions.length} of {options.length} options
                      {isLoadingMore && ' (loading more...)'}
                    </div>
                  )}
                  {!searchQuery && options.length > 20 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground border-b flex items-center gap-2">
                      <span>
                        {options.length} options loaded
                        {isLoadingMore ? ' — loading more...' : hasMore && autoLoadComplete ? ' — more available below' : ' — type to search'}
                      </span>
                      {isLoadingMore && <Loader2 className="h-3 w-3 animate-spin" />}
                    </div>
                  )}
                  {!searchQuery && options.length <= 20 && isLoadingMore && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground border-b flex items-center gap-2">
                      <span>Loading more options...</span>
                      <Loader2 className="h-3 w-3 animate-spin" />
                    </div>
                  )}
                  {filteredOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={handleComboboxSelect}
                      className="overflow-hidden"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col min-w-0 overflow-hidden">
                        <span className="truncate">{option.label}</span>
                        {option.description && (
                          <span className="text-xs text-muted-foreground truncate">{option.description}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                  {/* Load more button when there are more results available */}
                  {hasMore && autoLoadComplete && !isLoadingMore && (
                    <div className="px-2 py-2 border-t">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLoadMore();
                        }}
                      >
                        Load more...
                      </Button>
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
